
import React, { useState } from 'react';
import { FileUploadProgress } from '../types';
import { X, Minimize2, Maximize2, CheckCircle, AlertCircle, Loader2, File } from 'lucide-react';

interface UploadWidgetProps {
  uploads: FileUploadProgress[];
  onClose: (id: string) => void;
}

export const UploadWidget = ({ uploads, onClose }: UploadWidgetProps) => {
  const [isMinimized, setIsMinimized] = useState(false);

  if (uploads.length === 0) return null;

  const completedCount = uploads.filter(u => u.status === 'completed').length;
  const errorCount = uploads.filter(u => u.status === 'error').length;
  const inProgressCount = uploads.length - completedCount - errorCount;

  return (
    <div className={`fixed bottom-0 right-10 z-[200] w-96 bg-[#1e1e1e] border border-zinc-700 rounded-t-lg shadow-2xl transition-all duration-300 transform ${isMinimized ? 'translate-y-[calc(100%-48px)]' : 'translate-y-0'}`}>
      {/* Header */}
      <div 
        className="flex items-center justify-between px-4 py-3 bg-[#2d2d2d] rounded-t-lg cursor-pointer hover:bg-[#363636] transition-colors border-b border-zinc-700"
        onClick={() => setIsMinimized(!isMinimized)}
      >
        <span className="text-sm font-medium text-white">
          {inProgressCount > 0 ? `Uploading ${inProgressCount} item${inProgressCount !== 1 ? 's' : ''}...` : 'Uploads complete'}
        </span>
        <div className="flex items-center gap-2">
           <button className="text-zinc-400 hover:text-white p-1 rounded hover:bg-white/10" onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }}>
             {isMinimized ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
           </button>
           {completedCount + errorCount === uploads.length && (
              <button 
                className="text-zinc-400 hover:text-white p-1 rounded hover:bg-white/10"
                onClick={(e) => { e.stopPropagation(); uploads.forEach(u => onClose(u.id)); }}
              >
                <X size={16}/>
              </button>
           )}
        </div>
      </div>

      {/* List */}
      <div className="overflow-y-auto max-h-[300px] bg-[#1e1e1e]">
         {uploads.map((upload) => (
           <div key={upload.id} className="group relative flex items-center gap-3 p-3 hover:bg-[#2d2d2d] transition-colors border-b border-zinc-800 last:border-0">
              <div className="w-8 h-8 rounded bg-zinc-800 flex items-center justify-center text-zinc-400 shrink-0">
                 <File size={16} />
              </div>
              
              <div className="flex-1 min-w-0">
                 <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-zinc-200 truncate pr-2">{upload.fileName}</span>
                    {upload.status === 'uploading' && (
                       <button onClick={(e) => { e.stopPropagation(); onClose(upload.id); }} className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-white transition-opacity">
                          <X size={14} />
                       </button>
                    )}
                 </div>
                 
                 {/* Progress Bar Container */}
                 <div className="h-1 w-full bg-zinc-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-300 ${upload.status === 'error' ? 'bg-red-500' : upload.status === 'completed' ? 'bg-green-500' : 'bg-blue-500'}`} 
                      style={{ width: `${upload.progress}%` }}
                    ></div>
                 </div>
              </div>

              <div className="shrink-0 w-6 flex justify-end">
                 {upload.status === 'uploading' && <span className="text-xs text-zinc-500 font-mono">{upload.progress}%</span>}
                 {upload.status === 'completed' && <CheckCircle size={18} className="text-green-500" />}
                 {upload.status === 'error' && <AlertCircle size={18} className="text-red-500" />}
              </div>
           </div>
         ))}
      </div>
    </div>
  );
};
